import { useState, useEffect } from 'react';
import { useError } from '../../context/ErrorContext';
import { howItWorksService } from '../../services/howItWorksService';
import { Save, X, Plus, Trash2 } from 'lucide-react';
import { nanoid } from 'nanoid';

const HowItWorksContentManager = ({ setEditingHowItWorks, updatePageLastUpdated }) => {
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showError } = useError();

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        const content = await howItWorksService.getHowItWorksContent();
        setPageContent(content);
      } catch (error) {
        showError('Failed to load "How It Works" page content: ' + error.message, 'error');
        setPageContent(null);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [showError]);

  const handleChange = (sectionId, index, field, value) => {
    setPageContent(prevContent => {
      const newContent = { ...prevContent };
      // Check if the update is for a top-level field (like pageTitle or pageSubtitle)
      if (sectionId === null) {
        newContent[field] = value;
      } else {
        // Find the specific section to update
        const section = newContent.sections.find(s => s.id === sectionId);
        if (section) {
          section.items[index][field] = value;
        }
      }
      return newContent;
    });
  };

  const handleSaveContent = async () => {
    try {
      setLoading(true);
      const updatedContent = await howItWorksService.createOrUpdateHowItWorksContent(pageContent);
      setPageContent(updatedContent);
      updatePageLastUpdated('How It Works', updatedContent.lastUpdated);
      showError('"How It Works" page updated successfully!', 'success');
    } catch (error) {
      showError('Failed to save "How It Works" page content: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const addStep = () => {
    setPageContent(prevContent => {
      const newContent = { ...prevContent };
      const stepsSection = newContent.sections.find(s => s.id === 'steps');
      if (stepsSection) {
        const newStepNumber = (stepsSection.items.length + 1).toString().padStart(2, '0');
        stepsSection.items.push({
          id: nanoid(),
          number: newStepNumber,
          title: "New Step",
          description: "Enter a description for the new step."
        });
      }
      return newContent;
    });
  };

  const removeStep = (indexToRemove) => {
    setPageContent(prevContent => {
      const newContent = { ...prevContent };
      const stepsSection = newContent.sections.find(s => s.id === 'steps');
      if (stepsSection) {
        stepsSection.items.splice(indexToRemove, 1);
        // Renumber the steps
        stepsSection.items.forEach((step, index) => {
          step.number = (index + 1).toString().padStart(2, '0');
        });
      }
      return newContent;
    });
  };

  if (!pageContent) {
    return <div className="flex items-center justify-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-xl">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-8 w-8 text-accent dark:text-accent mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-lg text-gray-700 dark:text-gray-300">Loading How it Works...</p>
        </div>
      </div>;
  }

  const stepsSection = pageContent.sections.find(s => s.id === 'steps');

  return (
    <div className="bg-white dark:bg-gray-800 p-1 md:p-6 rounded-xl shadow-2xl dark:shadow-xl border border-gray-100 dark:border-gray-700 animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Edit "How It Works" Page
        </h2>
        <button
          onClick={() => setEditingHowItWorks(false)}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition"
        >
          <X size={24} />
        </button>
      </div>

      <div className="space-y-6">
        {/* Page Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Page Title</label>
          <input
            type="text"
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
            value={pageContent.pageTitle}
            onChange={(e) => handleChange(null, -1, 'pageTitle', e.target.value)}
          />
        </div>

        {/* Page Subtitle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Page Subtitle</label>
          <textarea
            rows="2"
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
            value={pageContent.pageSubtitle}
            onChange={(e) => handleChange(null, -1, 'pageSubtitle', e.target.value)}
          ></textarea>
        </div>

        {/* Steps Section */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Steps</h3>
          <div className="space-y-4">
            {stepsSection.items.map((step, index) => (
              <div key={step.id} className="relative p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700 shadow-sm transition-colors duration-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-bold text-accent dark:text-accent">Step {step.number}</span>
                  <button
                    onClick={() => removeStep(index)}
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                    aria-label="Remove step"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                    value={step.title}
                    onChange={(e) => handleChange('steps', index, 'title', e.target.value)}
                  />
                </div>
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <textarea
                    rows="3"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                    value={step.description}
                    onChange={(e) => handleChange('steps', index, 'description', e.target.value)}
                  ></textarea>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={addStep}
            className="mt-4 flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg shadow-sm hover:bg-gray-300 dark:hover:bg-gray-600 transition duration-200 font-semibold text-sm"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Step
          </button>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSaveContent}
          className="flex items-center px-6 py-2.5 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-accent/80 dark:hover:bg-accent/80 transition duration-200 font-semibold"
          disabled={loading}
        >
          <Save className="w-4 h-4 mr-2" /> Save Changes
        </button>
      </div>
    </div>
  );
};

export default HowItWorksContentManager;